import { signOut } from 'firebase/auth';
import { useState, type CSSProperties } from 'react';
import { auth } from '../firebase/client';
import { joinCompanyCallable } from '../auth/phirtiAuth';
import { userFacingErrorMessage } from '../lib/userFacingError';
import {
  displayNameValidationError,
  DISPLAY_NAME_HELPER_TEXT,
  DISPLAY_NAME_RULES_HINT,
  hasInvalidDisplayNameCharacters,
  isValidDisplayName,
  MAX_DISPLAY_NAME_LENGTH,
  MIN_DISPLAY_NAME_LENGTH,
} from '../lib/displayNameValidation';

const labelStyle: CSSProperties = {
  display: 'flex',
  flexDirection: 'column',
  fontSize: 13,
  gap: 6,
  marginBottom: 14,
};

export function WorkspaceSetupPage() {
  const [joinCode, setJoinCode] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [displayNameError, setDisplayNameError] = useState<string | null>(null);

  const user = auth().currentUser;
  const phone = user?.phoneNumber?.trim();

  const canJoin =
    joinCode.trim().length > 0 &&
    isValidDisplayName(displayName) &&
    !busy;

  async function onJoin() {
    setErr(null);
    if (!joinCode.trim()) {
      setErr('Enter an invite or join code');
      return;
    }
    const nameErr = displayNameValidationError(displayName);
    if (nameErr) {
      setDisplayNameError(nameErr);
      return;
    }
    setDisplayNameError(null);
    setBusy(true);
    try {
      await joinCompanyCallable(joinCode, {
        phoneNumber: phone,
        displayName: displayName.trim(),
      });
      window.location.assign('/');
    } catch (e: unknown) {
      setErr(userFacingErrorMessage(e, { fallback: 'Something went wrong. Please try again.' }));
    } finally {
      setBusy(false);
    }
  }

  return (
    <div className="auth-screen">
      <div
        style={{
          minHeight: '100%',
          padding: 24,
          background: 'linear-gradient(180deg, #e8f2ff 0%, #f5f7fb 40%)',
        }}
      >
        <div style={{ maxWidth: 440, margin: '48px auto' }}>
          <h1 style={{ fontSize: 26 }}>Phirti</h1>
          <p style={{ color: 'var(--ph-muted)', lineHeight: 1.5 }}>
            Join your team&apos;s Company Workspace with an invite code. To create a new
            Company Workspace, use the Phirti mobile app.
          </p>

          <section
            style={{
              background: '#fff',
              borderRadius: 12,
              padding: 20,
              marginTop: 24,
              boxShadow: '0 4px 24px rgba(0,0,0,0.06)',
            }}
          >
            <h2 style={{ fontSize: 17, marginTop: 0 }}>Join a company with a code</h2>

            <label style={labelStyle}>
              Invite or join code
              <input
                className="ph-input"
                placeholder="Paste link or enter code"
                value={joinCode}
                onChange={(e) => {
                  setJoinCode(e.target.value);
                  if (err) setErr(null);
                }}
              />
            </label>

            <label style={labelStyle}>
              Display name
              <input
                className="ph-input"
                placeholder="e.g. Rahul Sharma"
                minLength={MIN_DISPLAY_NAME_LENGTH}
                maxLength={MAX_DISPLAY_NAME_LENGTH}
                value={displayName}
                onChange={(e) => {
                  const v = e.target.value;
                  setDisplayName(v);
                  if (!v.trim()) {
                    setDisplayNameError(null);
                    return;
                  }
                  if (hasInvalidDisplayNameCharacters(v)) {
                    setDisplayNameError('Use only letters, spaces, and hyphens.');
                    return;
                  }
                  if (displayNameError) setDisplayNameError(null);
                }}
                onBlur={() => {
                  if (!displayName.trim()) return;
                  setDisplayNameError(displayNameValidationError(displayName));
                }}
                autoCapitalize="words"
                autoCorrect="off"
                spellCheck={false}
                aria-invalid={displayNameError ? true : undefined}
              />
              {displayNameError ? (
                <span style={{ color: '#c00', fontSize: 12 }}>{displayNameError}</span>
              ) : null}
            </label>
            <p style={{ margin: '0 0 16px', fontSize: 13, color: 'var(--ph-muted)', lineHeight: 1.45 }}>
              {DISPLAY_NAME_RULES_HINT} {DISPLAY_NAME_HELPER_TEXT}
            </p>

            <button
              type="button"
              className="ph-btn"
              style={{ width: '100%' }}
              disabled={!canJoin}
              onClick={() => void onJoin()}
            >
              {busy ? 'Joining…' : 'Join'}
            </button>
            <button
              type="button"
              className="ph-btn ph-btn--outline"
              style={{ width: '100%', marginTop: 10 }}
              disabled={busy}
              onClick={() => void signOut(auth())}
            >
              Sign out
            </button>
          </section>

          {err ? (
            <p style={{ color: '#c00', marginTop: 16, textAlign: 'center' }}>{err}</p>
          ) : null}
        </div>
      </div>
    </div>
  );
}
